import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  Stack,
  Chip,
  useTheme,
  useMediaQuery,
  Divider
} from '@mui/material';
import {
  Lock as LockIcon,
  Security as SecurityIcon,
  Timer as TimerIcon,
  Token as TokenIcon,
  TrendingUp as TrendingUpIcon,
  Shield as ShieldIcon,
  AccountBalanceWallet as WalletIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Verified as VerifiedIcon,
  Speed as SpeedIcon,
  Support as SupportIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { styled } from '@mui/material/styles';
import { ThemeProvider } from '@mui/material/styles';
import { themeCustom } from '../utils/themeUtils';

// Netflix-inspired color palette with blue and black
const netflixColors = {
  primary: '#0071EB', // Netflix blue (changed from red)
  secondary: '#0056B3', // Darker blue
  background: '#000000', // Black
  surface: '#141414', // Dark surface
  text: '#FFFFFF', // White text
  textSecondary: '#B3B3B3', // Light gray
  accent: '#F5F5F1', // Light cream
};

const HeroSection = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #000000 0%, #141414 50%, #1a1a2e 100%)',
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(circle at 20% 80%, rgba(0, 113, 235, 0.15) 0%, transparent 50%), 
      radial-gradient(circle at 80% 20%, rgba(0, 86, 179, 0.15) 0%, transparent 50%),
      radial-gradient(circle at 50% 50%, rgba(255, 165, 0, 0.05) 0%, transparent 70%)
    `,
    pointerEvents: 'none',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, transparent 0%, rgba(0, 113, 235, 0.02) 50%, transparent 100%)',
    pointerEvents: 'none',
  }
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #141414 0%, rgba(20, 20, 20, 0.9) 100%)',
  backdropFilter: 'blur(20px)',
  borderRadius: 20,
  border: '1px solid rgba(255, 255, 255, 0.1)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #0071EB 0%, #0056B3 100%)',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&:hover': {
    boxShadow: '0 16px 48px rgba(0, 0, 0, 0.4)',
    transform: 'translateY(-8px)',
    borderColor: '#0071EB',
    '&::before': {
      opacity: 1,
    },
  },
}));

const SecurityCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #0071EB 0%, #0056B3 100%)',
  color: 'white',
  borderRadius: 20,
  border: '1px solid rgba(255, 255, 255, 0.2)',
  boxShadow: '0 8px 32px rgba(0, 113, 235, 0.3)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    boxShadow: '0 16px 48px rgba(0, 113, 235, 0.4)',
    transform: 'translateY(-4px)',
  },
}));

const CTAButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #0071EB 0%, #0056B3 100%)',
  color: '#fff',
  fontWeight: 700,
  textTransform: 'none',
  padding: '16px 32px',
  borderRadius: '16px',
  fontSize: '18px',
  boxShadow: '0 8px 25px rgba(0, 113, 235, 0.3)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: 'linear-gradient(135deg, #0056B3 0%, #003D82 100%)',
    transform: 'translateY(-3px)',
    boxShadow: '0 12px 35px rgba(0, 113, 235, 0.4)',
  },
}));

const Home = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const features = [
    {
      icon: <SecurityIcon sx={{ fontSize: 40, color: '#0071EB' }} />,
      title: 'Secure Locking',
      description: 'Advanced smart contracts ensure your tokens are locked securely with time-based releases.',
      color: '#0071EB'
    },
    {
      icon: <TimerIcon sx={{ fontSize: 40, color: '#0071EB' }} />,
      title: 'Time-Based Unlocking',
      description: 'Set custom lock durations from hours to years with automatic unlock mechanisms.',
      color: '#0071EB'
    },
    {
      icon: <TokenIcon sx={{ fontSize: 40, color: '#00D474' }} />,
      title: 'Multi-Chain Support',
      description: 'Lock tokens on Ethereum and Binance Smart Chain with seamless cross-chain functionality.',
      color: '#00D474'
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 40, color: '#0071EB' }} />,
      title: 'Profit Tracking',
      description: 'Monitor your locked tokens and track potential profits with real-time analytics.',
      color: '#0071EB'
    },
    {
      icon: <ShieldIcon sx={{ fontSize: 40, color: '#0071EB' }} />,
      title: 'Audited Contracts',
      description: 'All smart contracts undergo rigorous security audits for maximum protection.',
      color: '#0071EB'
    },
    {
      icon: <WalletIcon sx={{ fontSize: 40, color: '#0071EB' }} />,
      title: 'Easy Integration',
      description: 'Connect any Web3 wallet and start locking tokens in just a few clicks.',
      color: '#0071EB'
    }
  ];

  const securityFeatures = [
    {
      icon: <VerifiedIcon sx={{ fontSize: 32, color: 'white' }} />,
      title: 'Audited Smart Contracts',
      description: 'All contracts undergo rigorous security audits by leading blockchain security firms.'
    },
    {
      icon: <ShieldIcon sx={{ fontSize: 32, color: 'white' }} />,
      title: 'Multi-Signature Security',
      description: 'Advanced multi-signature mechanisms ensure maximum protection of your assets.'
    },
    {
      icon: <LockIcon sx={{ fontSize: 32, color: 'white' }} />,
      title: 'Time-Lock Mechanisms',
      description: 'Impossible to unlock before the specified time, even by contract owners.'
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 32, color: 'white' }} />,
      title: 'Instant Execution',
      description: 'Lightning-fast transactions with maximum efficiency and security.'
    }
  ];

  const howItWorks = [
    {
      step: '01',
      title: 'Connect Wallet',
      description: 'Connect your Web3 wallet (MetaMask, WalletConnect, etc.) securely to the platform.'
    },
    {
      step: '02',
      title: 'Select Token',
      description: 'Choose the token you want to lock and specify the amount and unlock time.'
    },
    {
      step: '03',
      title: 'Confirm Lock',
      description: 'Review your lock parameters and confirm the transaction on your wallet.'
    },
    {
      step: '04',
      title: 'Monitor & Unlock',
      description: 'Track your locked tokens and unlock them automatically when the time comes.'
    }
  ];

  const stats = [
    { number: '10K+', label: 'Tokens Locked' },
    { number: '$50M+', label: 'Total Value Locked' },
    { number: '99.9%', label: 'Uptime' },
    { number: '24/7', label: 'Support' }
  ];

  return (
    <ThemeProvider theme={themeCustom}>
      <Box sx={{ background: '#000000', color: '#FFFFFF' }}>
        {/* Hero Section */}
        <HeroSection>
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <Typography
                    variant={isMobile ? 'h3' : 'h2'}
                    fontWeight={800}
                    gutterBottom
                    sx={{
                      color: '#FFFFFF',
                      mb: 2,
                      fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
                      textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8), 0 0 20px rgba(0, 113, 235, 0.5)',
                      filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5))'
                    }}
                  >
                    Secure Token Locking
                    <br />
                    <span style={{ 
                      fontSize: '0.7em', 
                      fontWeight: 600, 
                      color: '#FFA500',
                      textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)'
                    }}>
                      for the Future
                    </span>
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ 
                      mb: 3, 
                      lineHeight: 1.7, 
                      color: '#B3B3B3',
                      maxWidth: '600px',
                      fontSize: isMobile ? '16px' : '20px'
                    }}
                  >
                    Lock your tokens securely with time-based releases. Multi-chain support, 
                    advanced security, and seamless user experience for modern DeFi.
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2, 
                    mb: 4,
                    flexWrap: 'wrap'
                  }}>
                    <Chip 
                      label="Ethereum & BSC" 
                      sx={{ 
                        background: 'linear-gradient(135deg, #0071EB 0%, #0056B3 100%)',
                        color: '#FFFFFF',
                        fontWeight: 600,
                        px: 2,
                        py: 1
                      }} 
                    />
                    <Chip 
                      label="Secure Unlock" 
                      sx={{ 
                        background: 'linear-gradient(135deg, #FFA500 0%, #FF8C00 100%)',
                        color: '#FFFFFF',
                        fontWeight: 600,
                        px: 2,
                        py: 1
                      }} 
                    />
                    <Chip 
                      label="Secure & Audited" 
                      sx={{ 
                        background: 'linear-gradient(135deg, #00D474 0%, #00B060 100%)',
                        color: '#FFFFFF',
                        fontWeight: 600,
                        px: 2,
                        py: 1
                      }} 
                    />
                  </Box>
                  <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
                    <CTAButton
                      component={Link}
                      to="/lock-tokens"
                      endIcon={<ArrowForwardIcon />}
                    >
                      Start Locking Tokens
                    </CTAButton>
                    <Button
                      variant="outlined"
                      size="large"
                      component={Link}
                      to="/docs"
                      sx={{
                        borderWidth: '2px',
                        borderRadius: '16px',
                        padding: '16px 32px',
                        fontSize: '18px',
                        fontWeight: 600,
                        color: '#FFFFFF',
                        borderColor: '#0071EB',
                        '&:hover': {
                          borderColor: '#0071EB',
                          backgroundColor: 'rgba(0, 113, 235, 0.1)'
                        }
                      }}
                    >
                      Learn More
                    </Button>
                  </Stack>
                </motion.div>
              </Grid>
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  <Box
                    sx={{
                      background: 'linear-gradient(135deg, #141414 0%, rgba(20, 20, 20, 0.95) 100%)',
                      borderRadius: 4,
                      p: 4,
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(0, 113, 235, 0.3)',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: 'linear-gradient(90deg, #0071EB 0%, #FFA500 50%, #00D474 100%)',
                      }
                    }}
                  >
                    <Typography 
                      variant="h5" 
                      color="#FFFFFF" 
                      gutterBottom 
                      fontWeight={700}
                      sx={{ 
                        textAlign: 'center',
                        mb: 3,
                        background: 'linear-gradient(90deg, #FFFFFF 0%, #0071EB 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}
                    >
                      Platform Statistics
                    </Typography>
                    <Grid container spacing={3}>
                      {stats.map((stat, index) => (
                        <Grid item xs={6} key={index}>
                          <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6, delay: index * 0.1 }}
                          >
                            <Box 
                              textAlign="center"
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                background: 'rgba(0, 113, 235, 0.05)',
                                border: '1px solid rgba(0, 113, 235, 0.1)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  background: 'rgba(0, 113, 235, 0.1)',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 4px 20px rgba(0, 113, 235, 0.2)'
                                }
                              }}
                            >
                              <Typography 
                                variant="h4" 
                                color="#0071EB" 
                                fontWeight={800}
                                sx={{ 
                                  mb: 1,
                                  background: 'linear-gradient(135deg, #0071EB 0%, #FFA500 100%)',
                                  WebkitBackgroundClip: 'text',
                                  WebkitTextFillColor: 'transparent'
                                }}
                              >
                                {stat.number}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: '#B3B3B3',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}
                              >
                                {stat.label}
                              </Typography>
                            </Box>
                          </motion.div>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </HeroSection>

        {/* Security Section */}
        <Box sx={{ py: 8, background: 'linear-gradient(135deg, #000000 0%, #141414 100%)' }}>
          <Container maxWidth="lg">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Typography 
                variant="h3" 
                textAlign="center" 
                color="#FFFFFF" 
                fontWeight={700} 
                gutterBottom
                sx={{
                  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5))'
                }}
              >
                Security First
              </Typography>
              <Typography variant="h6" textAlign="center" sx={{ color: '#B3B3B3', mb: 6 }}>
                Your security is our top priority. We implement industry-leading security measures.
              </Typography>
            </motion.div>
            <Grid container spacing={4}>
              {securityFeatures.map((feature, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <SecurityCard>
                      <CardContent sx={{ p: 3, textAlign: 'center' }}>
                        <Box sx={{ mb: 2 }}>
                          {feature.icon}
                        </Box>
                        <Typography variant="h6" gutterBottom fontWeight={600}>
                          {feature.title}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          {feature.description}
                        </Typography>
                      </CardContent>
                    </SecurityCard>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* How It Works Section */}
        <Box sx={{ py: 8, background: '#141414' }}>
          <Container maxWidth="lg">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Typography 
                variant="h3" 
                textAlign="center" 
                color="#FFFFFF" 
                fontWeight={700} 
                gutterBottom
                sx={{
                  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5))'
                }}
              >
                How It Works
              </Typography>
              <Typography variant="h6" textAlign="center" sx={{ color: '#B3B3B3', mb: 6 }}>
                Simple 4-step process to secure your tokens
              </Typography>
            </motion.div>
            <Grid container spacing={4}>
              {howItWorks.map((step, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <FeatureCard>
                      <CardContent sx={{ p: 4, textAlign: 'center' }}>
                        <Chip
                          label={step.step}
                          sx={{ 
                            mb: 2, 
                            fontSize: '16px', 
                            fontWeight: 600,
                            background: 'linear-gradient(135deg, #0071EB 0%, #0056B3 100%)',
                            color: '#FFFFFF'
                          }}
                        />
                        <Typography variant="h6" gutterBottom fontWeight={600} color="#FFFFFF">
                          {step.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                          {step.description}
                        </Typography>
                      </CardContent>
                    </FeatureCard>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Features Section */}
        <Box sx={{ py: 8, background: '#000000' }}>
          <Container maxWidth="lg">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Typography 
                variant="h3" 
                textAlign="center" 
                color="#FFFFFF" 
                fontWeight={700} 
                gutterBottom
                sx={{
                  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5))'
                }}
              >
                Why Choose Token Locker?
              </Typography>
              <Typography variant="h6" textAlign="center" sx={{ color: '#B3B3B3', mb: 6 }}>
                Advanced features designed for modern DeFi users
              </Typography>
            </motion.div>
            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <FeatureCard>
                      <CardContent sx={{ p: 4, textAlign: 'center' }}>
                        <Box sx={{ mb: 3 }}>
                          {feature.icon}
                        </Box>
                        <Typography variant="h6" gutterBottom fontWeight={600} color="#FFFFFF">
                          {feature.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                          {feature.description}
                        </Typography>
                      </CardContent>
                    </FeatureCard>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* CTA Section */}
        <Box sx={{ py: 8, background: 'linear-gradient(135deg, #000000 0%, #141414 100%)' }}>
          <Container maxWidth="md">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Box textAlign="center">
                <Typography 
                  variant="h3" 
                  color="#FFFFFF" 
                  fontWeight={700} 
                  gutterBottom
                  sx={{
                    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                    filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5))'
                  }}
                >
                  Ready to Secure Your Tokens?
                </Typography>
                <Typography variant="h6" sx={{ color: '#B3B3B3', mb: 4 }}>
                  Join thousands of users who trust Token Locker for their DeFi needs
                </Typography>
                <CTAButton
                  component={Link}
                  to="/lock-tokens"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                >
                  Get Started Now
                </CTAButton>
              </Box>
            </motion.div>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default Home; 